import { useMemo } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { ChevronDown } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { parseCampaignName } from '../cells';
import { classifyCampaign } from '@/hooks/usePerformanceData';
import { cn } from '@/lib/utils';
import type { PlatformScope } from '@/lib/performance/types';
import {
  campaignsDataAtom,
  consolidatedPerformanceDateRangeAtom,
  consolidatedPerformanceGroupByAtom,
  consolidatedPerformanceComparisonAtom,
  consolidatedPerformanceCampaignTypesAtom,
  consolidatedPerformanceCampaignIdsAtom,
  consolidatedPerformanceIncludeExternalAtom,
} from '@/store';

// NOTE: these four option sets are duplicated from performance-v2.tsx for now;
// #5994 tracks extracting them into a shared module so both surfaces import one copy.
const OVERVIEW_DATE_OPTIONS = [
  { value: 7, label: '7 days' },
  { value: 14, label: '14 days' },
  { value: 30, label: '30 days' },
  { value: 60, label: '60 days' },
  { value: 90, label: '90 days' },
  { value: 180, label: '180 days' },
  { value: 365, label: '365 days' },
  { value: 0, label: 'All time' },
];

const GROUP_BY_OPTIONS = [
  { value: 'day', label: 'Daily' },
  { value: 'week', label: 'Weekly' },
  { value: 'month', label: 'Monthly' },
];

const getComparisonOptions = (days: number) => [
  { value: 'none', label: 'No comparison' },
  { value: 'previous', label: days > 0 ? `vs Previous ${days} days` : 'vs Previous period' },
  { value: 'previous-week', label: 'vs Previous week' },
  { value: 'previous-month', label: 'vs Previous month' },
  { value: 'previous-year', label: 'vs Previous year' },
];

const CAMPAIGN_TYPE_TABS = [
  { value: 'all', label: 'All' },
  { value: 'brand', label: 'Brand' },
  { value: 'prospecting', label: 'Prosp.' },
  { value: 'competitor', label: 'Comp.' },
];

/**
 * Consolidated Performance filter toolbar (#5971). Sits below the tab bar. The
 * date range applies to every platform in scope; the campaign filter and
 * comparison are Google concepts and disable when the scope is Meta-only. All
 * state lives in shared atoms so the Overview (and, later, the other tabs —
 * #5994) can read it without prop-drilling. Export is intentionally omitted
 * here — it will be revisited with the Reports work (#5994).
 */
// Tabs whose content renders comparison deltas (#6276): Overview metric cards,
// Campaigns by-campaign cells, Creative & Ads' Ads sub-view. Everywhere else
// the control is disabled instead of silently doing nothing.
const COMPARISON_TABS = new Set(['overview', 'campaigns', 'creative-ads']);

export function ConsolidatedFilters({ scope, activeTab }: { scope: PlatformScope; activeTab: string }) {
  const googleInScope = scope !== 'meta';
  const comparisonUsable = googleInScope && COMPARISON_TABS.has(activeTab);
  // Group By aggregates the Overview's performance-over-time chart; the table
  // tabs have no time axis (#6276).
  const groupByUsable = activeTab === 'overview';

  const [dateRange, setDateRange] = useAtom(consolidatedPerformanceDateRangeAtom);
  const [groupBy, setGroupBy] = useAtom(consolidatedPerformanceGroupByAtom);
  const [comparison, setComparison] = useAtom(consolidatedPerformanceComparisonAtom);
  const [campaignTypes, setCampaignTypes] = useAtom(consolidatedPerformanceCampaignTypesAtom);
  const [selectedCampaignIds, setSelectedCampaignIds] = useAtom(consolidatedPerformanceCampaignIdsAtom);
  const [includeExternal, setIncludeExternal] = useAtom(consolidatedPerformanceIncludeExternalAtom);

  const allCampaigns = useAtomValue(campaignsDataAtom);
  // #6128 — the campaign picker lists every campaign the surface shows, in any state; the old
  // LIVE/PAUSED filter was the same accidental narrowing fixed in usePerformanceData.
  const activeCampaigns = allCampaigns;
  const typeFilter = useMemo<(type: string) => boolean>(() => {
    if (campaignTypes.size === 0 || campaignTypes.has('all')) return () => true;
    return (type: string) => campaignTypes.has(type);
  }, [campaignTypes]);
  const filteredCampaigns = useMemo(
    () => activeCampaigns.filter((c) => typeFilter(classifyCampaign(c))),
    [activeCampaigns, typeFilter],
  );

  const toggleCampaignType = (value: string) =>
    setCampaignTypes((prev) => {
      if (value === 'all') return new Set(['all']);
      const next = new Set(prev);
      next.delete('all');
      if (next.has(value)) next.delete(value);
      else next.add(value);
      if (next.size === 0) next.add('all');
      return next;
    });

  return (
    <div className="flex items-center gap-2">
      {/* Campaign Type + Campaign filter (Google) */}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className="h-9 text-gray-900" disabled={!googleInScope}>
            {(() => {
              if (selectedCampaignIds.size > 0) {
                return `${selectedCampaignIds.size} Campaign${selectedCampaignIds.size !== 1 ? 's' : ''}`;
              }
              if (campaignTypes.has('all') || campaignTypes.size === 0) return 'All Campaigns';
              const selected = CAMPAIGN_TYPE_TABS.filter((t) => campaignTypes.has(t.value));
              if (selected.length <= 2) return selected.map((t) => t.label).join(', ') + ' Campaigns';
              return `${selected.length} Types`;
            })()}
            <ChevronDown className="ml-2 h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 p-0">
          <div className="p-2">
            <div className="flex flex-wrap gap-1.5">
              {CAMPAIGN_TYPE_TABS.map((t) => {
                const isAll = t.value === 'all';
                const isActive = isAll
                  ? campaignTypes.has('all') || campaignTypes.size === 0
                  : campaignTypes.has(t.value);
                return (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => toggleCampaignType(t.value)}
                    className={cn(
                      'px-3 py-1 rounded-full text-xs font-medium border transition-colors',
                      isActive
                        ? 'bg-[#290BD9] border-[#290BD9] text-white'
                        : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50',
                    )}
                  >
                    {t.label}
                  </button>
                );
              })}
              {/* External is orthogonal to campaign type (a campaign is Brand/Prosp/Comp AND
                  managed-or-not), so it reads as an additive include/exclude, set apart by a divider. */}
              <span className="mx-0.5 self-stretch w-px bg-gray-200" aria-hidden />
              <button
                type="button"
                onClick={() => setIncludeExternal(!includeExternal)}
                title="Include external campaigns not managed by Glitch"
                className={cn(
                  'px-3 py-1 rounded-full text-xs font-medium border transition-colors',
                  includeExternal
                    ? 'bg-[#290BD9] border-[#290BD9] text-white'
                    : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50',
                )}
              >
                External
              </button>
            </div>
          </div>
          <Separator />
          <div className="max-h-[200px] overflow-y-auto p-1">
            {filteredCampaigns.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-gray-400">
                No campaigns available for this filter
              </div>
            ) : (
              <>
                <div
                  className="flex items-center space-x-2 px-2 py-1.5 hover:bg-gray-100 rounded-sm cursor-pointer"
                  onClick={() => setSelectedCampaignIds(new Set())}
                >
                  <Checkbox
                    checked={selectedCampaignIds.size === 0 || filteredCampaigns.every((c) => selectedCampaignIds.has(c.id))}
                    onCheckedChange={() => setSelectedCampaignIds(new Set())}
                    className="data-[state=checked]:bg-[#290BD9] data-[state=checked]:border-[#290BD9]"
                  />
                  <span className="text-sm font-medium">All Campaigns</span>
                </div>
                {filteredCampaigns.map((c) => {
                  const isSelected = selectedCampaignIds.size === 0 || selectedCampaignIds.has(c.id);
                  return (
                    <div
                      key={c.id}
                      className="flex items-center space-x-2 px-2 py-1.5 hover:bg-gray-100 rounded-sm cursor-pointer"
                      onClick={() =>
                        setSelectedCampaignIds((prev) => {
                          const next = new Set(prev.size === 0 ? filteredCampaigns.map((fc) => fc.id) : prev);
                          if (next.has(c.id)) next.delete(c.id);
                          else next.add(c.id);
                          // Back to everything selected → reset to empty (means "all").
                          if (filteredCampaigns.every((fc) => next.has(fc.id))) return new Set();
                          return next;
                        })
                      }
                    >
                      <Checkbox
                        checked={isSelected}
                        className="data-[state=checked]:bg-[#290BD9] data-[state=checked]:border-[#290BD9]"
                      />
                      <span className="text-sm truncate">{parseCampaignName(c.name)}</span>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {/* Comparison period (Google) — disabled on tabs without delta rendering (#6276) */}
      <Select value={comparison} onValueChange={setComparison} disabled={!comparisonUsable}>
        <SelectTrigger
          className="w-44 bg-white h-9 text-gray-900 font-medium"
          title={comparisonUsable ? undefined : 'Comparison applies to the Overview, Campaigns, and Creative & Ads tabs.'}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {getComparisonOptions(dateRange).map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Date range + grouping (all platforms) */}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className="h-9 text-gray-900">
            {OVERVIEW_DATE_OPTIONS.find((d) => d.value === dateRange)?.label || 'All time'},{' '}
            {GROUP_BY_OPTIONS.find((g) => g.value === groupBy)?.label}
            <ChevronDown className="ml-2 h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-0">
          <div className="flex">
            <div className="w-36 border-r border-gray-200 p-2">
              <p className="text-xs text-gray-500 px-2 py-1">Date Range</p>
              {OVERVIEW_DATE_OPTIONS.map((opt) => (
                <div
                  key={opt.value}
                  className="flex items-center space-x-2 px-2 py-1.5 hover:bg-gray-100 rounded-sm cursor-pointer"
                  onClick={() => setDateRange(opt.value)}
                >
                  <Checkbox checked={dateRange === opt.value} className="pointer-events-none" />
                  <span className="text-sm">{opt.label}</span>
                </div>
              ))}
            </div>
            <div
              className={cn('w-32 p-2', !groupByUsable && 'opacity-40')}
              title={groupByUsable ? undefined : 'Grouping applies to the Overview chart.'}
            >
              <p className="text-xs text-gray-500 px-2 py-1">Group By</p>
              {GROUP_BY_OPTIONS.map((opt) => (
                <div
                  key={opt.value}
                  className={cn(
                    'flex items-center space-x-2 px-2 py-1.5 rounded-sm',
                    groupByUsable ? 'hover:bg-gray-100 cursor-pointer' : 'cursor-not-allowed',
                  )}
                  onClick={() => groupByUsable && setGroupBy(opt.value as 'day' | 'week' | 'month')}
                >
                  <Checkbox checked={groupBy === opt.value} className="pointer-events-none" />
                  <span className="text-sm">{opt.label}</span>
                </div>
              ))}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
